import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { IconRegistryService } from '../../services/icon-registry.service';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import { AuthenticationService } from '../../../user/authentication.service';
import { BreakpointObserverService } from '../../services/breakpoint-observer.service';
import { UserAccessService } from '../../../user/access/user-access.service';
import { MatTooltip } from '@angular/material/tooltip';
import { MatNavList } from '@angular/material/list';
import { AsyncPipe, NgIf, NgTemplateOutlet } from '@angular/common';
import { IconComponent } from '../icon/icon.component';
import { filter, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import {
  SALES_FORECAST_BASE_PATH,
  salesForecastPath,
} from '../../../sales-forecast/sales-forecast.routes-data';
import { LOGO_HORIZONTAL_DATA_URI } from '../../services/inline-icons.generated';
import { BLOG_URL } from '../../util/external-links';

/**
 * One destination in the primary rail, rendered by both the desktop rail and
 * the mobile bar.
 *
 * `label` and `shortLabel` differ because the mobile bar gives each item a
 * 32px column: "PDF report" fits the rail and has to become "Report" there.
 * `activePrefix` is the path that lights the item up, which is not always
 * `link` - sales forecast links into one industry but owns the whole subtree.
 */
interface NavItem {
  icon: string;
  label: string;
  shortLabel: string;
  link: string;
  activePrefix: string;
  tooltip: string;
}

@Component({
  selector: 'app-navigation-menu',
  templateUrl: './navigation-menu.component.html',
  styleUrls: ['./navigation-menu.component.scss', 'navigation-mobile.scss'],
  imports: [
    NgIf,
    NgTemplateOutlet,
    MatNavList,
    RouterLink,
    MatTooltip,
    RouterLinkActive,
    AsyncPipe,
    IconComponent,
  ],
})
export class NavigationMenuComponent
  implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy
{
  @ViewChildren('moreMenuToggle')
  moreMenuToggles!: QueryList<ElementRef<HTMLButtonElement>>;

  readonly logoSrc = LOGO_HORIZONTAL_DATA_URI;
  readonly blogUrl = BLOG_URL;

  readonly navItems: readonly NavItem[] = [
    {
      icon: 'map',
      label: 'Map',
      shortLabel: 'Map',
      link: '/',
      activePrefix: '/',
      tooltip: 'Explore the interactive map',
    },
    {
      icon: 'ai_chat',
      label: 'AI chat',
      shortLabel: 'AI chat',
      link: '/chat',
      activePrefix: '/chat',
      tooltip: 'Ask AI about any location',
    },
    {
      icon: 'report',
      label: 'PDF report',
      shortLabel: 'Report',
      link: '/reports',
      activePrefix: '/reports',
      tooltip: 'Download a full location analysis as PDF',
    },
    {
      icon: 'site-selection',
      label: 'Sales forecast',
      shortLabel: 'Sales',
      link: '/' + salesForecastPath('my-business'),
      activePrefix: '/' + SALES_FORECAST_BASE_PATH,
      tooltip: 'Choose a perfect location for your business',
    },
  ];

  public isMoreMenuOpen = false;

  private updatePointerSubject = new Subject<void>();
  private destroy$ = new Subject<void>();
  private lastUpdateTime = 0;
  private updateThreshold = 100; // Only update every 100ms

  constructor(
    private iconRegistry: IconRegistryService,
    private router: Router,
    private route: ActivatedRoute,
    public authService: AuthenticationService,
    public accessService: UserAccessService,
    public breakpointObserverService: BreakpointObserverService
  ) {
    this.iconRegistry.initNavigationIcons();
  }

  ngOnInit(): void {
    // Listen for route changes to update the pointer position
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.triggerPointerUpdate();
      });

    // Set up debounced pointer updates
    this.updatePointerSubject
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(() => {
        this.updatePointerPosition();
      });
  }

  ngAfterViewInit(): void {
    // Initial position of the pointer after view is initialized
    setTimeout(() => this.triggerPointerUpdate(), 500);
  }

  ngAfterViewChecked(): void {
    // Throttle updates to avoid excessive calculations
    const now = Date.now();
    if (now - this.lastUpdateTime > this.updateThreshold) {
      this.lastUpdateTime = now;
      this.triggerPointerUpdate();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private triggerPointerUpdate(): void {
    this.updatePointerSubject.next();
  }

  /**
   * Slides the desktop rail's highlight behind whichever link is active.
   *
   * Desktop only: the mobile bar has no room under a labelled item for a
   * marker, and the blue icon-plus-label already says which tab you are on.
   */
  updatePointerPosition(): void {
    if (this.breakpointObserverService.isMobile.value) return;

    const pointer = document.querySelector('.desktop-pointer') as HTMLElement;
    const navPanel = document.querySelector(
      '.navigation-menu-panel'
    ) as HTMLElement;
    if (!pointer || !navPanel) return;

    const activeLink = document.querySelector(
      '.navigation-menu-panel .navmenu-links a.active-link'
    ) as HTMLElement | null;

    if (!activeLink) {
      pointer.style.opacity = '0';
      return;
    }

    const rect = activeLink.getBoundingClientRect();
    const navPanelRect = navPanel.getBoundingClientRect();

    pointer.style.top = `${rect.top - navPanelRect.top + rect.height / 2 - pointer.offsetHeight / 2}px`;
    pointer.style.opacity = '0.1';
  }

  public toggleMoreMenu(): void {
    this.isMoreMenuOpen = !this.isMoreMenuOpen;
  }

  public closeMoreMenu(): void {
    this.isMoreMenuOpen = false;
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    if (!this.isMoreMenuOpen) return;
    const target = event.target as HTMLElement;
    if (!target.closest('.more-menu-toggle, .more-menu-panel')) {
      this.closeMoreMenu();
    }
  }

  @HostListener('document:keydown.escape')
  public onMoreMenuEscape(): void {
    if (!this.isMoreMenuOpen) return;
    this.closeMoreMenu();
    this.moreMenuToggles?.first?.nativeElement.focus();
  }

  get accountLabel(): string {
    return this.authService.hasAccessToken() ? 'Profile' : 'Log in';
  }

  /**
   * The map is the only item whose prefix is `/`, which every path starts
   * with, so it gets the exact-plus-map-query-params match instead.
   */
  isActive(item: NavItem): boolean {
    if (item.activePrefix === '/') {
      return this.isActiveWithQueryParams('/');
    }
    return this.router.url.split('?')[0].startsWith(item.activePrefix);
  }

  //check if url corresponds to route including query params for map
  isActiveWithQueryParams(route: string): boolean {
    const currentRoute = this.router.url.split('?')[0];
    const currentQueryParams = this.route.snapshot.queryParams;

    if (currentRoute !== route) {
      return false;
    }

    if (Object.keys(currentQueryParams).length === 0) {
      return true;
    }

    if (currentQueryParams.hasOwnProperty('ll')) {
      return true;
    }

    return false;
  }
}
